import { CommonModule } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { ControlContainer, FormGroup, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../csr-web-api.service';
import {
  ChallengeMediaPlaceholder,
  onChallengeImageError,
  resolveChallengeMediaSrc
} from './challenge-media.util';

@Component({
  selector: 'app-image-url-field',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressBarModule
  ],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  templateUrl: './image-url-field.component.html',
  styleUrl: './image-url-field.component.scss'
})
export class ImageUrlFieldComponent {
  private readonly api = inject(CsrWebApiService);
  private readonly controlContainer = inject(ControlContainer, { optional: true });

  @Input({ required: true }) challengeId!: string;
  @Input({ required: true }) controlName!: string;
  @Input() label = 'Image URL';
  @Input() placeholderType: ChallengeMediaPlaceholder = 'judge';
  @Input() displayUrl: string | null | nullish = null;
  @Input() previewUrl: string | null | nullish = null;

  uploading = false;
  uploadError = '';

  get form(): FormGroup {
    return this.controlContainer?.control as FormGroup;
  }

  get storedValue(): string {
    return (this.form?.get(this.controlName)?.value as string | null | undefined) ?? '';
  }

  get imageSrc(): string {
    return resolveChallengeMediaSrc(
      this.previewUrl ?? this.displayUrl,
      this.storedValue,
      this.placeholderType
    );
  }

  onImageError(event: Event): void {
    onChallengeImageError(event, this.placeholderType);
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.form) {
      return;
    }

    this.uploading = true;
    this.uploadError = '';
    try {
      const response = await firstValueFrom(this.api.uploadCareerChallengeMedia(this.challengeId, file));
      this.form.get(this.controlName)?.setValue(response.key);
      this.previewUrl = response.url;
    } catch {
      this.uploadError = 'Upload failed. Try again or paste a URL.';
    } finally {
      this.uploading = false;
    }
  }
}

type nullish = null | undefined;